import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate, useRouter, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Checkbox,
  Input,
  Label,
  useToast,
  FormField,
} from '@asus-common/fe-ui-shadcn'
import { LuArrowRight } from 'react-icons/lu'
import { MdErrorOutline } from 'react-icons/md'

import { Pages } from '@/shared/config/pages'
import { sanitizeReturnTo } from '@/shared/auth/return-to'
import { ButtonWithTooltip, PasswordInput } from '@/features/auth/components'
import { useLogin } from '@/features/auth/hooks'
import { useAuthUiStore } from '@/features/auth/store'
import { loginSchema, type LoginFormValues } from '../../model'

export function LoginForm() {
  const navigate = useNavigate({ from: Pages.LOGIN })
  const router = useRouter()
  const { returnTo } = useSearch({ from: Pages.LOGIN })

  const { t } = useTranslation(['login', 'common'])
  const { toast } = useToast()
  const { authentication, resolveError } = useLogin()

  const openRequestPasswordResetModal = useAuthUiStore(
    s => s.openRequestPasswordResetModal,
  )
  const openExpiredPasswordModal = useAuthUiStore(
    s => s.openExpiredPasswordModal,
  )

  const {
    control,
    handleSubmit,
    setError,
    formState: { isValid, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { username: '', password: '', remember: false },
  })

  const onSubmit = handleSubmit(async ({ username, password, remember }) => {
    try {
      await authentication.mutateAsync(
        { username, password, remember },
        {
          onSuccess(outcome) {
            if (outcome.kind === 'password_reset_required') {
              navigate({
                to: Pages.RESET_PASSWORD_WITH_TOKEN(outcome.resetToken),
              })
              openExpiredPasswordModal()
            } else {
              const target = sanitizeReturnTo(returnTo)
              if (target) {
                router.history.push(target)
              } else {
                navigate({ to: Pages.DASHBOARD })
              }
            }
          },
        },
      )
    } catch (error) {
      const result = resolveError(error)

      if (result.display === 'toast') {
        toast({
          title: t(result.title),
          description: t(result.key),
          status: 'error',
        })
      } else {
        setError('username', { message: '' })
        setError('password', { message: result.key })
      }
    }
  })

  return (
    <form onSubmit={onSubmit} noValidate className="mt-6 flex flex-col gap-4">
      <Controller
        name="username"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <FormField>
            <Label htmlFor="inputEmail">{t('username')}</Label>
            <Input
              id="inputEmail"
              type="text"
              autoComplete="username"
              size="md"
              maxLength={100}
              disabled={isSubmitting}
              invalid={!!error}
              errorMessage={error?.message ? t(error.message) : undefined}
              endIcon={
                error ? (
                  <MdErrorOutline data-testid="error-icon" aria-hidden />
                ) : undefined
              }
              {...field}
              data-test="input-email"
            />
          </FormField>
        )}
      />

      <Controller
        name="password"
        control={control}
        render={({ field: { onChange, name }, fieldState: { error } }) => (
          <FormField>
            <Label htmlFor="inputPassword">{t('password')}</Label>
            <PasswordInput
              id="inputPassword"
              autoComplete="current-password"
              disabled={isSubmitting}
              error={error}
              name={name}
              onChange={onChange}
              data-test="input-password"
            />
          </FormField>
        )}
      />

      <div className="my-6 flex flex-wrap justify-end gap-4">
        <Controller
          name="remember"
          control={control}
          render={({ field }) => (
            <FormField className="flex-1 flex-row items-center gap-2">
              <Checkbox
                id="remember"
                size="md"
                checked={field.value}
                onChange={checked => field.onChange(checked === true)}
                data-test="checkbox-remember"
              />

              <Label htmlFor="remember" size="md" className="cursor-pointer">
                {t('remember_me')}
              </Label>
            </FormField>
          )}
        />

        <Button
          className="flex-1"
          type="button"
          variant="ghost"
          size="md"
          data-cy="forgot-password"
          onClick={openRequestPasswordResetModal}
          data-test="button-forgot-password"
        >
          {t('forgot_password')}
        </Button>

        <div className="flex w-full flex-col gap-1">
          <ButtonWithTooltip
            id="loginSubmit"
            label={t('btn_login')}
            tooltipContent={t('btn_login_tooltip')}
            isValid={isValid}
            isSubmitting={isSubmitting}
            endIcon={<LuArrowRight />}
            data-test="button-login-submit"
          />
        </div>
      </div>
    </form>
  )
}
